import { create } from "zustand";
import { persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { MainNavigationPrimaryMenuItemKeys } from "../config/menuItemsPrimary";
import { MainNavigationSecondaryMenuItemKeys } from "../config/menuItemsSecondary";
import { MAIN_NAVIGATION_PINNED_GROUP_ID } from "../config/usePinnedGroup";
import type { MainNavigationStore } from "./types";

const STORE_VERSION = 3;
// Raw means they are before check access rights
const ALL_RAW_MENU_ITEM_IDS = new Set([
  ...Object.values(MainNavigationPrimaryMenuItemKeys),
  ...Object.values(MainNavigationSecondaryMenuItemKeys),
]);

const useMainNavigationStore = create<MainNavigationStore>()(
  persist(
    immer((set) => ({
      isSidebarExpanded: true,
      defaultViewItemId: undefined,
      pinnedItemIds: [],
      mobileFullScreen: false,

      expandedGroupIds: new Set<string>([MAIN_NAVIGATION_PINNED_GROUP_ID]),
      itemCounts: {},
      activeDropdownGroupId: undefined,

      toggleGroupExpanded: (groupId: string) => {
        set((state) => {
          if (state.expandedGroupIds.has(groupId)) {
            state.expandedGroupIds.delete(groupId);
          } else {
            state.expandedGroupIds.add(groupId);
          }
        });
      },

      setItemCount: (itemId: string, count: number) => {
        set((state) => {
          if (count > 0) {
            state.itemCounts[itemId] = count;
          } else {
            delete state.itemCounts[itemId];
          }
        });
      },
      pinMenuItem: (itemId: string) => {
        set((state) => {
          if (!state.pinnedItemIds.includes(itemId)) {
            state.pinnedItemIds.push(itemId);
          }
        });
      },
      unpinMenuItem: (itemId: string) => {
        set((state) => {
          const index = state.pinnedItemIds.indexOf(itemId);
          if (index > -1) {
            state.pinnedItemIds.splice(index, 1);
          }
        });
      },
      reorderPinnedMenuItems: (orderedItemIds: string[]) => {
        set((state) => {
          state.pinnedItemIds = orderedItemIds;
        });
      },
      moveItemUp: (itemId: string) => {
        set((state) => {
          const index = state.pinnedItemIds.indexOf(itemId);
          if (index <= 0) return;

          const item = state.pinnedItemIds.splice(index, 1)[0];
          state.pinnedItemIds.splice(index - 1, 0, item);
        });
      },
      moveItemDown: (itemId: string) => {
        set((state) => {
          const index = state.pinnedItemIds.indexOf(itemId);
          if (index === -1 || index >= state.pinnedItemIds.length - 1) return;

          const item = state.pinnedItemIds.splice(index, 1)[0];
          state.pinnedItemIds.splice(index + 1, 0, item);
        });
      },
      setDefaultViewItemId: (itemId: string | undefined) => {
        set({ defaultViewItemId: itemId });
      },
      setIsSidebarExpanded: (isExpanded: boolean) => {
        set({ isSidebarExpanded: isExpanded });
      },

      setActiveDropdownMenu: (dropdownMenuId: string) => {
        set({ activeDropdownGroupId: dropdownMenuId });
      },

      closeDropdownMenu: (dropdownMenuId?: string) => {
        set((state) => {
          if (!dropdownMenuId || state.activeDropdownGroupId === dropdownMenuId) {
            state.activeDropdownGroupId = undefined;
          }
        });
      },

      setMobileFullScreen: (mobileFullScreen: boolean) => {
        set({ mobileFullScreen });
      },
    })),
    {
      name: "mainNavigationStore",
      version: STORE_VERSION,
      partialize: (state) => ({
        pinnedItemIds: state.pinnedItemIds,
        defaultViewItemId: state.defaultViewItemId,
        isSidebarExpanded: state.isSidebarExpanded,
        expandedGroupIds: Array.from(state.expandedGroupIds),
      }),
      migrate: (persistedState, version) => {
        const persisted = persistedState as Partial<
          Omit<MainNavigationStore, "expandedGroupIds"> & { expandedGroupIds?: string[] }
        >;

        if (version < STORE_VERSION) {
          // Remove invalid pinned items that no longer exist in the navigation
          const validPinnedItems = (persisted.pinnedItemIds ?? []).filter((id: string) =>
            ALL_RAW_MENU_ITEM_IDS.has(id as never)
          );

          // Remove invalid default view if it doesn't exist
          const validDefaultView =
            persisted.defaultViewItemId &&
            ALL_RAW_MENU_ITEM_IDS.has(persisted.defaultViewItemId as never)
              ? persisted.defaultViewItemId
              : undefined;

          // Ensure pinned group is expanded by default (added in version 3)
          const expandedGroupIds = persisted.expandedGroupIds ?? [];
          if (!expandedGroupIds.includes(MAIN_NAVIGATION_PINNED_GROUP_ID)) {
            expandedGroupIds.push(MAIN_NAVIGATION_PINNED_GROUP_ID);
          }

          return {
            ...persisted,
            pinnedItemIds: validPinnedItems,
            defaultViewItemId: validDefaultView,
            expandedGroupIds,
          };
        }

        return persistedState;
      },
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<
          Omit<MainNavigationStore, "expandedGroupIds"> & { expandedGroupIds?: string[] }
        >;
        return {
          ...currentState,
          ...persisted,
          expandedGroupIds: new Set(persisted.expandedGroupIds ?? []),
        };
      },
    }
  )
);

export default useMainNavigationStore;
